import { useEffect, useRef } from 'react';

export const useClickOutsideAndKeyNavigation = (
  callback: (event: MouseEvent | TouchEvent | KeyboardEvent) => void,
  cycleThroughInputs = false
) => {
  const ref = useRef<HTMLDivElement | HTMLFormElement>(null);

  useEffect(() => {
    const getFocusElements = () => {
      const element = ref.current;
      return (
        element?.querySelectorAll<HTMLInputElement>(
          'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), area[href], [tabindex="0"], contenteditable, audio[controls], video[controls], summary'
        ) || []
      );
    };

    const handleTabKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Tab') {
        const focusElements = getFocusElements();
        const firstFocusElement = focusElements[0];
        const lastFocusElement = focusElements[focusElements.length - 1];

        if (event.shiftKey && document.activeElement === firstFocusElement) {
          event.preventDefault();
          cycleThroughInputs ? lastFocusElement?.focus() : callback(event);
        } else if (!event.shiftKey && document.activeElement === lastFocusElement) {
          event.preventDefault();
          cycleThroughInputs ? firstFocusElement?.focus() : callback(event);
        }
      }
    };

    const handleEscapeKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        callback(event);
      }
    };

    const handleClickOutside = (event: MouseEvent | TouchEvent): void => {
      const element = ref.current;
      if (!element) return;

      const consentModal = document.getElementById('onetrust-consent-sdk');
      const isClickInsideConsentModal = consentModal?.contains(event.target as Node);

      if (!element.contains(event.target as Node) && !isClickInsideConsentModal) {
        event.preventDefault();
        callback(event);
      }
    };

    document.addEventListener('keydown', handleTabKeyDown);
    document.addEventListener('keydown', handleEscapeKeyDown);
    document.addEventListener('mouseup', handleClickOutside);
    document.addEventListener('touchend', handleClickOutside);

    return () => {
      document.removeEventListener('keydown', handleTabKeyDown);
      document.removeEventListener('keydown', handleEscapeKeyDown);
      document.removeEventListener('mouseup', handleClickOutside);
      document.removeEventListener('touchend', handleClickOutside);
    };
  }, [callback, cycleThroughInputs]);

  return ref;
};
